import React, { useCallback, useEffect } from 'react';
import { useI18next } from 'gatsby-plugin-react-i18next';
import Cookies from 'js-cookie';
import { useTranslation } from 'react-i18next';
import { DEFAULT_LANGUAGE } from 'styles/theme';
import styled from 'styled-components';
import useOrganizeLinks from 'utils/organizeLinks';

const ElementComponent = styled.div.withConfig({ shouldForwardProp: (prop) => !['theme'].includes(prop) })`
	${({ theme }) =>
		theme.language !== 'ko' &&
		`
        & * {
            font-family: Lato !important;
        }
  `};
`;

const blackList = ['/voucher/'];

type LocaleBaseProps = {
	children: React.ReactNode;
	language?: string | null;
};

export const useLocale = () => {
	const { i18n } = useTranslation();
	const { changeLanguage, originalPath } = useI18next();
	const language = i18n.language;
	const isKo = language === 'ko';

	const handleLanguage = useCallback(
		(lang: string) => {
			if (language === lang) return;

			Cookies.set('locales-language', lang, { domain: process.env.GATSBY_COOKIE_HOST });

			// `originalPath` keeps Gatsby's file-system route template (`[id]`) for dynamic pages.
			// Use the concrete browser path so language switching preserves the actual detail ID.
			const currentPath =
				typeof window === 'undefined'
					? originalPath
					: language === 'ko'
						? window.location.pathname
						: window.location.pathname.replace(new RegExp(`^/${language}(?=/|$)`), '') || '/';

			return changeLanguage(lang, blackList.includes(currentPath) ? '/' : currentPath);
		},
		[changeLanguage, language, originalPath]
	);

	return { handleLanguage, isKo, language };
};

const LocaleBase = ({ children, language = DEFAULT_LANGUAGE }: LocaleBaseProps) => {
	const currentLanguage = language ?? DEFAULT_LANGUAGE;

	useOrganizeLinks(currentLanguage);

	useEffect(() => {
		Cookies.set('locales-language', currentLanguage, { domain: process.env.GATSBY_COOKIE_HOST });
	}, [currentLanguage]);

	return <ElementComponent>{children}</ElementComponent>;
};

export default LocaleBase;
